import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { IngestEnvelope, JsonSchemaBody } from '@collector/shared'

export type VisualizerMode = 'view' | 'edit'

interface Props {
  envelope: IngestEnvelope
  schema: JsonSchemaBody
  mode: VisualizerMode
  onChange?: (envelope: IngestEnvelope) => void
}

interface FieldDef {
  key: string
  title: string
  type: 'string' | 'number' | 'integer' | 'boolean' | 'enum' | 'unknown'
  enumValues?: string[]
  unit?: string
  description?: string
  required: boolean
  min?: number
  max?: number
}

/**
 * Renders an envelope against the JSON Schema that describes it. The renderer
 * walks the top-level `properties` map and produces a single field per
 * property. Nested objects/arrays are shown as JSON for now; v2 can recurse.
 *
 * In `edit` mode each field is wired to `onChange`; the parent controls
 * whether to POST the result to the server or re-encode it as a QR.
 */
export function DataSetVisualizer({ envelope, schema, mode, onChange }: Props) {
  const { t } = useTranslation()
  const fields = useMemo(() => extractFields(schema), [schema])

  const updateData = (key: string, value: unknown) => {
    if (!onChange) return
    onChange({ ...envelope, data: { ...envelope.data, [key]: value } })
  }
  const updatePatient = (key: 'name' | 'dob' | 'externalId', value: string) => {
    if (!onChange) return
    onChange({
      ...envelope,
      patient: { ...envelope.patient, [key]: value || undefined } as IngestEnvelope['patient'],
    })
  }

  return (
    <div className="space-y-6">
      <section className="card">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          {t('visualizer.patient')}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <PatientField
            label={t('visualizer.name')}
            value={envelope.patient.name}
            mode={mode}
            onChange={(v) => {
              updatePatient('name', v)
            }}
          />
          <PatientField
            label={t('visualizer.dateOfBirth')}
            value={envelope.patient.dob ?? ''}
            mode={mode}
            type="date"
            onChange={(v) => {
              updatePatient('dob', v)
            }}
          />
          <PatientField
            label={t('visualizer.externalId')}
            value={envelope.patient.externalId ?? ''}
            mode={mode}
            onChange={(v) => {
              updatePatient('externalId', v)
            }}
          />
        </div>
        <p className="mt-3 text-xs text-stone-500 dark:text-stone-400">
          {t('visualizer.capturedMeta', {
            capturedAt: envelope.capturedAt,
            tabletUid: envelope.tabletUid,
          })}
        </p>
      </section>

      <section className="card">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          {schema.title ?? t('visualizer.data')}
        </h2>
        {fields.length === 0 ? (
          <p className="text-sm text-stone-500 dark:text-stone-400">
            {t('visualizer.noProperties')}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {fields.map((f) => (
              <DataField
                key={f.key}
                field={f}
                value={envelope.data[f.key]}
                mode={mode}
                onChange={(v) => {
                  updateData(f.key, v)
                }}
              />
            ))}
          </div>
        )}
        <details className="mt-4">
          <summary className="cursor-pointer text-xs font-medium text-stone-500 dark:text-stone-400">
            {t('visualizer.rawPayload')}
          </summary>
          <pre className="mt-2 overflow-x-auto rounded bg-stone-50 p-3 text-xs dark:bg-stone-950 dark:text-stone-200">
            {JSON.stringify(envelope.data, null, 2)}
          </pre>
        </details>
      </section>
    </div>
  )
}

interface PatientFieldProps {
  label: string
  value: string
  mode: VisualizerMode
  type?: 'text' | 'date'
  onChange: (v: string) => void
}

function PatientField({ label, value, mode, type = 'text', onChange }: PatientFieldProps) {
  if (mode === 'view') {
    return (
      <div>
        <div className="text-xs uppercase text-stone-500 dark:text-stone-400">{label}</div>
        <div className="text-sm text-stone-900 dark:text-stone-100">{value || '—'}</div>
      </div>
    )
  }
  return (
    <div>
      <label className="label">{label}</label>
      <input
        className="input"
        type={type}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
      />
    </div>
  )
}

interface DataFieldProps {
  field: FieldDef
  value: unknown
  mode: VisualizerMode
  onChange: (v: unknown) => void
}

function DataField({ field, value, mode, onChange }: DataFieldProps) {
  if (mode === 'view') {
    return (
      <div>
        <div className="text-xs uppercase text-stone-500 dark:text-stone-400">
          {field.title}
          {field.required ? <span className="text-red-500 dark:text-red-400"> *</span> : null}
        </div>
        <div className="text-sm text-stone-900 dark:text-stone-100">
          {value === undefined || value === null
            ? '—'
            : typeof value === 'object'
              ? JSON.stringify(value)
              : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
                ? String(value)
                : JSON.stringify(value)}
          {field.unit ? (
            <span className="ml-1 text-stone-500 dark:text-stone-400">{field.unit}</span>
          ) : null}
        </div>
      </div>
    )
  }
  const labelText = field.title + (field.required ? ' *' : '')
  if (field.type === 'enum') {
    return (
      <div>
        <label className="label">{labelText}</label>
        <select
          className="input"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => {
            onChange(e.target.value || undefined)
          }}
        >
          <option value="">—</option>
          {(field.enumValues ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    )
  }
  if (field.type === 'boolean') {
    return (
      <div>
        <label className="label">{labelText}</label>
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={Boolean(value)}
          onChange={(e) => {
            onChange(e.target.checked)
          }}
        />
      </div>
    )
  }
  if (field.type === 'number' || field.type === 'integer') {
    return (
      <div>
        <label className="label">{labelText}</label>
        <input
          className="input"
          type="number"
          step={field.type === 'integer' ? 1 : 'any'}
          min={field.min}
          max={field.max}
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => {
            const v = e.target.value
            if (v === '') return onChange(undefined)
            const n = field.type === 'integer' ? Number.parseInt(v, 10) : Number.parseFloat(v)
            onChange(Number.isFinite(n) ? n : undefined)
          }}
        />
      </div>
    )
  }
  return (
    <div>
      <label className="label">{labelText}</label>
      <input
        className="input"
        type="text"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => {
          onChange(e.target.value)
        }}
      />
    </div>
  )
}

function extractFields(schema: JsonSchemaBody): FieldDef[] {
  const props = (schema as { properties?: Record<string, unknown> }).properties
  if (!props || typeof props !== 'object') return []
  const required = new Set(
    Array.isArray((schema as { required?: unknown[] }).required)
      ? (schema as { required: string[] }).required
      : [],
  )
  return Object.entries(props).map(([key, raw]) => {
    const def = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
    const enumValues = Array.isArray(def.enum) ? (def.enum as string[]) : undefined
    const type =
      enumValues && enumValues.length > 0
        ? 'enum'
        : def.type === 'integer'
          ? 'integer'
          : def.type === 'number'
            ? 'number'
            : def.type === 'boolean'
              ? 'boolean'
              : def.type === 'string'
                ? 'string'
                : 'unknown'
    return {
      key,
      title: typeof def.title === 'string' ? def.title : key,
      type,
      enumValues,
      unit: typeof def.unit === 'string' ? def.unit : undefined,
      description: typeof def.description === 'string' ? def.description : undefined,
      required: required.has(key),
      min: typeof def.minimum === 'number' ? def.minimum : undefined,
      max: typeof def.maximum === 'number' ? def.maximum : undefined,
    }
  })
}
